import api from './api';

// ============================================
// TYPES
// ============================================

export interface Product {
  id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  stock: number;
  status: 'active' | 'inactive';
  provider: string;
  image?: string;
  storeId?: string;
  storeName?: string;
  createdAt: string;
  iva?: number;
}

export interface ProductStats {
  total: number;
  active: number;
  categories: number;
  totalValue: number;
}

export interface Store {
  id: string;
  name: string;
  description?: string;
  logo?: string;
  address?: string;
  phone?: string;
  schedule?: string;
  latitude?: number;
  longitude?: number;
  typeId?: string;
  typeName?: string;
  entityId?: string;
  status: 'active' | 'inactive' | 'draft';
  montraVisivel?: number | boolean; // aparece no Pagali Shop (app e site); 0 = só Chat-to-Pay
  createdAt: string;
}

export interface StoreStats {
  total: number;
  active: number;
}

export interface StoreType {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  status: 'active' | 'inactive';
  count: number;
}

export interface Category {
  id: string;
  name: string;
  productCount: number;
}

export interface ShoppingPlan {
  id: string;
  name: string;
  price: number;
  features: number;
  stores: number;
  status: 'active' | 'inactive';
  createdAt?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  needsSetup?: boolean;
}

// ============================================
// SERVICE
// ============================================

class ShopService {
  // ============================================
  // PRODUCTS
  // ============================================

  async getProducts(params?: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    search?: string;
    storeId?: string;
  }): Promise<PaginatedResponse<Product>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.category) queryParams.append('category', params.category);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.storeId) queryParams.append('storeId', params.storeId);

      const response = await api.get(`/admin/shop/products?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get products error:', error);
      throw error;
    }
  }

  async getProductStats(): Promise<{ success: boolean; data: ProductStats }> {
    try {
      const response = await api.get('/admin/shop/products/stats');
      return response.data;
    } catch (error) {
      console.error('Get product stats error:', error);
      throw error;
    }
  }

  async createProduct(data: {
    name: string;
    description?: string;
    category?: string;
    price?: number;
    stock?: number;
    status?: 'active' | 'inactive' | 'draft';
    provider?: string;
    image?: string;
    storeId?: string;
  }): Promise<{ success: boolean; data: { id: string }; message: string }> {
    try {
      const response = await api.post('/admin/shop/products', data);
      return response.data;
    } catch (error) {
      console.error('Create product error:', error);
      throw error;
    }
  }

  async updateProduct(
    id: string,
    data: {
      name?: string;
      description?: string;
      category?: string;
      price?: number;
      stock?: number;
      status?: 'active' | 'inactive' | 'draft';
      provider?: string;
      image?: string;
      storeId?: string;
    }
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/products/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update product error:', error);
      throw error;
    }
  }

  async deleteProduct(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete product error:', error);
      throw error;
    }
  }

  // ============================================
  // CATEGORIES
  // ============================================

  async getCategories(): Promise<{ success: boolean; data: Category[] }> {
    try {
      const response = await api.get('/admin/shop/categories');
      return response.data;
    } catch (error) {
      console.error('Get categories error:', error);
      throw error;
    }
  }

  async getCategoriesWithDetails(): Promise<{ success: boolean; data: Category[]; needsSetup?: boolean }> {
    try {
      const response = await api.get('/admin/shop/categories/details');
      return response.data;
    } catch (error) {
      console.error('Get categories details error:', error);
      throw error;
    }
  }

  async createCategory(data: {
    name: string;
    description?: string;
    icon?: string;
    status?: 'active' | 'inactive' | 'draft';
  }): Promise<{ success: boolean; data: { id: string }; message: string }> {
    try {
      const response = await api.post('/admin/shop/categories', data);
      return response.data;
    } catch (error) {
      console.error('Create category error:', error);
      throw error;
    }
  }

  async updateCategory(
    id: string,
    data: {
      name?: string;
      description?: string;
      icon?: string;
      status?: 'active' | 'inactive' | 'draft';
    }
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/categories/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update category error:', error);
      throw error;
    }
  }

  async deleteCategory(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/categories/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete category error:', error);
      throw error;
    }
  }

  // ============================================
  // STORES
  // ============================================

  async getStores(params?: {
    page?: number;
    limit?: number;
    status?: string;
    typeId?: string;
    search?: string;
  }): Promise<PaginatedResponse<Store>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.typeId) queryParams.append('typeId', params.typeId);
      if (params?.search) queryParams.append('search', params.search);

      const response = await api.get(`/admin/shop/stores?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get stores error:', error);
      throw error;
    }
  }

  async getStoreStats(): Promise<{ success: boolean; data: StoreStats }> {
    try {
      const response = await api.get('/admin/shop/stores/stats');
      return response.data;
    } catch (error) {
      console.error('Get store stats error:', error);
      throw error;
    }
  }

  async createStore(data: {
    name: string;
    description?: string;
    logo?: string;
    address?: string;
    phone?: string;
    schedule?: string;
    latitude?: number;
    longitude?: number;
    typeId?: string;
    entityId?: string;
    status?: 'active' | 'inactive' | 'draft';
  }): Promise<{ success: boolean; data: { id: string }; message: string }> {
    try {
      const response = await api.post('/admin/shop/stores', data);
      return response.data;
    } catch (error) {
      console.error('Create store error:', error);
      throw error;
    }
  }

  async updateStore(
    id: string,
    data: {
      name?: string;
      description?: string;
      logo?: string;
      address?: string;
      phone?: string;
      schedule?: string;
      latitude?: number;
      longitude?: number;
      typeId?: string;
      entityId?: string;
      status?: 'active' | 'inactive' | 'draft';
    }
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/stores/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update store error:', error);
      throw error;
    }
  }

  async deleteStore(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/stores/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete store error:', error);
      throw error;
    }
  }

  // ============================================
  // STORE TYPES
  // ============================================

  async getStoreTypes(): Promise<{ success: boolean; data: StoreType[]; needsSetup?: boolean }> {
    try {
      const response = await api.get('/admin/shop/store-types');
      return response.data;
    } catch (error) {
      console.error('Get store types error:', error);
      throw error;
    }
  }

  async createStoreType(data: {
    name: string;
    description?: string;
    icon?: string;
    status?: 'active' | 'inactive' | 'draft';
  }): Promise<{ success: boolean; data: { id: string }; message: string }> {
    try {
      const response = await api.post('/admin/shop/store-types', data);
      return response.data;
    } catch (error) {
      console.error('Create store type error:', error);
      throw error;
    }
  }

  async updateStoreType(
    id: string,
    data: {
      name?: string;
      description?: string;
      icon?: string;
      status?: 'active' | 'inactive' | 'draft';
    }
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/store-types/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update store type error:', error);
      throw error;
    }
  }

  async deleteStoreType(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/store-types/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete store type error:', error);
      throw error;
    }
  }

  // ============================================
  // SHOPPING PLANS
  // ============================================

  async getPlans(): Promise<{ success: boolean; data: ShoppingPlan[]; needsSetup?: boolean }> {
    try {
      const response = await api.get('/admin/shop/plans');
      return response.data;
    } catch (error) {
      console.error('Get plans error:', error);
      throw error;
    }
  }

  async createPlan(data: {
    name: string;
    price?: number;
    features?: number;
    stores?: number;
    status?: 'active' | 'inactive' | 'draft';
  }): Promise<{ success: boolean; data: { id: string }; message: string }> {
    try {
      const response = await api.post('/admin/shop/plans', data);
      return response.data;
    } catch (error) {
      console.error('Create plan error:', error);
      throw error;
    }
  }

  async updatePlan(
    id: string,
    data: {
      name?: string;
      price?: number;
      features?: number;
      stores?: number;
      status?: 'active' | 'inactive' | 'draft';
    }
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/plans/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update plan error:', error);
      throw error;
    }
  }

  async deletePlan(id: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/admin/shop/plans/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete plan error:', error);
      throw error;
    }
  }

  // ============================================
  // SETUP
  // ============================================

  async setupTables(): Promise<{
    success: boolean;
    message: string;
    created: string[];
    updated: string[];
  }> {
    try {
      const response = await api.post('/admin/shop/setup');
      return response.data;
    } catch (error) {
      console.error('Setup tables error:', error);
      throw error;
    }
  }
}

export default new ShopService();
